// Import
import React, { useMemo, useState } from "react";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { useFetchTable } from "@hooks/useFetchDataTable";
import Table from "@components/Table";
import ButtonCst from "@components/ButtonCst";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { ColumnsType } from "src/interfaces/Common";
import { PagesID } from "@utils/utilsConfigurations";
import { ArrowCircleLeft } from "@mui/icons-material";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import { useFilters } from "@context/filters";
import { useAuth } from "@context/auth";
import { type MRT_TableInstance } from "material-react-table";

// Interface
interface AssociationActivityCstInterface {
  setCloseAssociation?: any;
  association: any;
  setAssociation: any;
  enableMultiRowSelection?: boolean;
  service: string;
  pageID: PagesID;
  title: string;
  filter?: string;
  multiChoice?: boolean;
  callBackAssociation?: (item: any, list?: any) => void;
  defaultFilters?: { [x: string]: any };
  manageConfigs?: { [x: string]: any };
  labelKey?: string;
  customBackAction?: () => void;
  enableFullScreenToggle?: boolean;
  removeCTAfromRowOrTable?: string[];
  durationKey?: boolean; // valore da settare se vogliamo dare al form anche la DURATA dell'elemento selezionato nella grid
  hiddenFilters?: boolean;
  associationDisabled?: any;
  renderTopToolbar?:
    | React.ReactNode
    | ((props: { table: MRT_TableInstance<{}> }) => React.ReactNode);
}

// eslint-disable-next-line no-empty-pattern
const AssociationTableCst: React.FC<AssociationActivityCstInterface> = ({
  setCloseAssociation,
  association,
  setAssociation,
  enableMultiRowSelection = true,
  service,
  pageID,
  title,
  filter,
  callBackAssociation,
  defaultFilters = {},
  manageConfigs = {},
  labelKey,
  customBackAction,
  enableFullScreenToggle,
  removeCTAfromRowOrTable = ["add"],
  durationKey,
  hiddenFilters,
  associationDisabled = null,
  renderTopToolbar,
}) => {
  const { t } = useTranslation(["form", "common"]);
  const theme = useTheme();
  const { user } = useAuth();
  const { filters, setFilters } = useFilters();
  const filterId = `${user?.id_user}_${pageID}`;

  const formattedElements = () => {
    let obj = {};
    association?.forEach((item: any) => {
      let labelName = labelKey ? labelKey : item.label ? "label" : "name";
      let labelId = item.id ? "id" : "code";
      let id = `${item[labelId]}||${item[labelName]}`;
      if (durationKey) {
        labelName = "label";
        labelId = "id";
        id = `${item["id"]}||${item["label"]}||${item["duration"] || item["activity_duration"] || 0}||${item["type"] || ""}`;
      }
      obj = {
        ...obj,
        [id]: true,
      };
    });
    return obj;
  };
  const [selected, setSelected] = useState<any>(formattedElements());

  let newRoles = {};
  if (filters[filterId]?.roles?.length > 0) {
    newRoles = {
      roles: filters[filterId].roles
        ?.map((type: any) => {
          return (type?.value).toString().toUpperCase();
        })
        .join(","),
    };
  }

  const { columns, dataTable, actionsTopToolbar, actionForRow } = useFetchTable(
    service,
    pageID,
    Object.keys(filters[filterId] || {}).length > 0
      ? { ...defaultFilters, ...filters[filterId], ...newRoles }
      : { ...defaultFilters, pageIndex: 0, pageSize: 20 },
  );

  const columnsConfigsManaged = useMemo(() => {
    if (manageConfigs && Object.keys(manageConfigs).length > 0) {
      const test = columns?.map((column: ColumnsType) => {
        return { ...column, ...{ ...manageConfigs[column.accessorKey] } };
      });
      return test;
    } else {
      return columns;
    }
  }, [columns]);

  useEffect(() => {
    setSelected(formattedElements());
    if (filter === "teacher") {
      dataTable.output = dataTable.output.filter(
        (item: any) => item.roles === filter,
      );
    }
    // clear dei filtri associati a "filterId" se esistono
    if (filters[filterId]) {
      setFilters(filterId, {});
    }
  }, []);

  function getKeysIdTitle(originalRow: any) {
    switch (pageID) {
      case "faculties":
        return `${originalRow?.id_user}||${originalRow?.name} ${originalRow?.surname}`;
      case "corporate.customers":
        return `${originalRow?.id_user}||${originalRow?.name} ${originalRow?.surname}`;
      case "stakeholders.corporate.customers":
        return `${originalRow?.id_user}||${originalRow?.name} ${originalRow?.surname}`;
      case "dashboard.corporate.customers":
        return `${originalRow?.id_user}||User`;
      case "corporate.teachersArea.edition.association":
        return `${originalRow?.id}||${originalRow?.edition_code}`;
      case "dashboard.enablement":
        return `${originalRow?.dashboardId}||${originalRow?.name}`;
      case "enrollment.subscribed.edition":
        return `${originalRow?.idUser}||${originalRow?.userFirstName} ${originalRow?.userLastName}`;
      case "corporate.editions.tutors-faculties":
        return `${originalRow?.id_user}||${originalRow?.name} ${originalRow?.surname}`;
      case "lti_accounts_association":
        return `${originalRow?.idCorporateAccount}||${originalRow?.accountName}`;
      case "cost-centers":
      case "contracts":
        return `${originalRow?.id}||${originalRow?.sap_code}`;
      case "accounts":
        return `${originalRow?.id}||${originalRow?.sap_number} - ${originalRow?.name}`;
      case "customers":
        return `${originalRow?.id}||${originalRow?.business_name}`;
      case "suppliers":
        return `${originalRow?.id}||${originalRow?.business_name} - ${originalRow?.sap_code}`;
      case "budget":
        return `${originalRow?.id}||${originalRow?.year}`;
      case "projects.categorizations.categories-cost":
        return `${originalRow?.id}||${originalRow?.macro_category?.title}||${originalRow?.available_budget}`;
      case "corporate.physical-editions":
        return `${originalRow?.id}||${originalRow?.edition_name}`;
      case "corporate.foundedTraining.managePlan.plan":
        return `${originalRow?.id}||${originalRow?.plan_title}`;
      case "users":
        return `${originalRow?.id_user}||${originalRow?.name} ${originalRow?.surname}`;
      case "corporate.initiative.leaderboard-cluster.scoring.association.community":
        return `${originalRow?.cluster.id}||${originalRow?.cluster.name}`;
      default:
        if (durationKey) {
          return `${originalRow?.id}||${originalRow?.title || originalRow?.edition_name}||${originalRow?.duration || originalRow?.activity_duration || 0}||${originalRow?.type || ""}`;
        } else {
          return `${originalRow?.id}||${originalRow?.title || originalRow?.name}`;
        }
    }
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          paddingBottom: "24px",
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "24px",
          },
        }}
      >
        <Link
          component="button"
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => {
            customBackAction ? customBackAction() : setCloseAssociation(false);
          }}
        >
          <ArrowCircleLeft sx={{ fontSize: "32px" }} />
          <Typography sx={{ fontWeight: "600" }}>{t("backButton")}</Typography>
        </Link>

        <Box
          sx={{
            display: "flex",
            gap: "24px",
            flexDirection: "column",
            [theme.breakpoints.up("sm")]: {
              width: "100%",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            },
            [theme.breakpoints.up("md")]: {
              width: "auto",
            },
          }}
        >
          <Typography variant="h4" component={"h3"}>
            {title}
          </Typography>
          <ButtonCst
            id={"save-button"}
            variant={"contained"}
            size={"medium"}
            sx={{
              minWidth: "140px",
              [theme.breakpoints.down("lg")]: {
                minWidth: "80px",
              },
              [theme.breakpoints.down("sm")]: {
                minWidth: "100%",
              },
            }}
            onClick={() => {
              if (callBackAssociation) {
                callBackAssociation(selected, dataTable.output);
              } else {
                setAssociation(selected);
                setCloseAssociation(false);
              }
            }}
          >
            {t("saveButton")}
          </ButtonCst>
        </Box>
      </Box>
      {!columnsConfigsManaged ? (
        <FormLoadingStateCst />
      ) : (
        <Box>
          <Table
            id={pageID}
            data={
              dataTable?.output ||
              dataTable?.content ||
              dataTable?.dto?.sub_scopes ||
              dataTable?.categorizations ||
              dataTable?.users ||
              dataTable ||
              []
            }
            columns={columnsConfigsManaged || []}
            configurationTopToolbar={actionsTopToolbar?.filter(
              (el) => !removeCTAfromRowOrTable.includes(el.type),
            )}
            {...(renderTopToolbar && { renderTopToolbar })}
            configurationActionForRow={actionForRow?.map((action: any) =>
              action.type === "delete"
                ? { ...action, callBack: () => null }
                : action,
            )}
            totalPages={
              dataTable?.metadata?.totalElements ||
              dataTable?.metadata?.total_elements ||
              0
            }
            enableMultiRowSelection={enableMultiRowSelection}
            enableRowSelection={(row: any) => {
              let itemFound = association?.find(
                (item: any) =>
                  item?.id.toString() ===
                    (row?.original?.id?.toString() ||
                      row?.original?.dashboardId?.toString() ||
                      row?.original?.idUser?.toString()) && !!item?.disabled,
              );
              if (itemFound) {
                return !itemFound;
              } else {
                if (associationDisabled) {
                  return !associationDisabled.includes(
                    Number(row?.original?.id_user),
                  );
                } else {
                  return true;
                }
              }
            }}
            enableRowActions={false}
            enableFullScreenToggle={enableFullScreenToggle}
            selectedRows={selected}
            onRowSelectionChange={(item: any) => {
              setSelected(item);
            }}
            getRowId={(originalRow: any) => {
              return getKeysIdTitle(originalRow);
            }}
            numberOfActions={1}
            hiddenFilters={hiddenFilters}
            modAssociationEnabled={true}
          />
        </Box>
      )}
    </>
  );
};

export default AssociationTableCst;
